import { useState, useEffect } from "react";
import { AlertTriangle, Clock, CreditCard, Loader2 } from "lucide-react";
import { billing } from "./lib/api.js";

const shekels = (agorot) =>
  "₪" + Math.round((agorot || 0) / 100).toLocaleString("he-IL");

/**
 * פס מצב המנוי.
 *
 * מוצג רק כשיש מה לומר: בסוף תקופת הניסיון, כשהחשבון עבר לקריאה בלבד,
 * או כשמנוי ששולם עומד להסתיים. חשבון בתוך ניסיון ארוך לא רואה כלום —
 * התראה שמופיעה כל יום מפסיקה להיקרא הרבה לפני שהיא רלוונטית.
 *
 * כשהמסלול שנתי ובעל הפלטפורמה הגדיר סליקה, הפס כולל גם את כפתורי
 * התשלום: מנוי לשנה, ומבצע לשנתיים אם הוגדר.
 */
export default function TrialBanner() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    let alive = true;
    billing.me().then((r) => alive && setS(r)).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!s) return null;

  /* הפס מתחיל להופיע מהתזכורת הראשונה שבעל הפלטפורמה קבע; 14 הוא רק
     ברירת מחדל לשרת ישן שעוד לא מחזיר את השדה. */
  const remindFrom = s.remindDays1 ?? 14;

  const readOnly = !s.canWrite;
  /* ימי חסד: הפקיעה עברה אבל עדיין מותר לערוך — פס נפרד ודחוף. */
  const grace = !!s.inGrace;
  const ending = !grace && s.inTrial && s.trialDaysLeft != null && s.trialDaysLeft <= remindFrom;
  /* מנוי ששולם ומתקרב לסופו — תזכורת חידוש. */
  const paidLeft = s.paid && s.paidUntil
    ? Math.ceil((new Date(s.paidUntil) - new Date()) / 86400000) : null;
  const renewing = paidLeft != null && paidLeft <= remindFrom;

  if (!readOnly && !grace && !ending && !renewing) return null;

  const pay = s.payment || {};
  const canPay = (pay.available || pay.deal2y) && s.status !== "suspended";

  const checkout = async (plan) => {
    setBusy(plan); setNote("");
    try {
      const r = await billing.checkout(plan);
      if (r.url) {
        window.open(r.url, "_blank", "noopener");
        setNote("עמוד התשלום נפתח בחלון חדש. אחרי התשלום המנוי יעודכן — אם החשבון לא התעדכן תוך יום, פנו אלינו.");
      } else {
        setNote(r.message || "הבקשה נרשמה.");
      }
    } catch (e) {
      setNote(e.message || "פתיחת התשלום נכשלה.");
    }
    setBusy("");
  };

  const tone = readOnly || grace
    ? { bg: "#FBEDE9", fg: "#7A2E1E", Icon: AlertTriangle }
    : { bg: "#FBF2E3", fg: "#6B4E12", Icon: Clock };

  const yearly = s.billingInterval === "yearly";
  const twoYearFull = 2 * (pay.yearAgorot || 0);
  const saving = pay.deal2y && twoYearFull > pay.twoYearAgorot
    ? twoYearFull - pay.twoYearAgorot : 0;

  return (
    <div dir="rtl" className="rounded-xl px-4 py-3 mb-3" style={{ background: tone.bg, color: tone.fg }}>
      <div className="flex items-start gap-2">
        <tone.Icon size={16} className="mt-0.5 shrink-0" />
        <div className="text-sm">
          {readOnly ? (
            <>
              <strong>
                {s.status === "suspended" ? "החשבון הושעה." : "תקופת הניסיון הסתיימה."}
              </strong>{" "}
              הנתונים שלכם שמורים וזמינים לצפייה, אבל אי אפשר לערוך עד לחידוש המנוי.
            </>
          ) : grace ? (
            <>
              <strong>{s.paidUntil ? "המנוי הסתיים." : "תקופת הניסיון הסתיימה."}</strong>{" "}
              נותרו {s.graceDaysLeft} ימי חסד לעריכה — לאחריהם החשבון יעבור
              למצב צפייה בלבד. הנתונים שלכם שמורים במלואם.
            </>
          ) : renewing && !ending ? (
            <>
              <strong>המנוי מסתיים בעוד {paidLeft} ימים.</strong>
              {yearly && " חידוש עכשיו מאריך מסוף התקופה — לא מפסידים יום."}
            </>
          ) : (
            <>
              <strong>נותרו {s.trialDaysLeft} ימי ניסיון</strong>
              {s.units > 0 && (
                <span>
                  {" · "}{s.units} יחידות מאוכלסות
                  {s.chargeAgorot > 0 &&
                    ` · ${shekels(s.chargeAgorot)} ${yearly ? "לשנה" : "לחודש"} אחרי הניסיון`}
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {canPay && (
        <div className="flex flex-wrap items-center gap-2 mt-2.5 pr-6">
          {pay.available && (
            <button onClick={() => checkout("1y")} disabled={!!busy}
              className="rounded-lg px-3.5 py-2 text-sm flex items-center gap-1.5"
              style={{ background: "#0F5132", color: "#fff", opacity: busy ? 0.7 : 1 }}>
              {busy === "1y" ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
              מנוי לשנה — {shekels(pay.yearAgorot)}
            </button>
          )}
          {pay.deal2y && (
            <button onClick={() => checkout("2y")} disabled={!!busy}
              className="rounded-lg px-3.5 py-2 text-sm flex items-center gap-1.5"
              style={{ border: "1.5px solid #0F5132", color: "#0F5132", background: "#fff", opacity: busy ? 0.7 : 1 }}>
              {busy === "2y" ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
              מבצע שנתיים — {shekels(pay.twoYearAgorot)}
              {saving > 0 && <span className="text-[11px]">(חיסכון {shekels(saving)})</span>}
            </button>
          )}
          {pay.pending && (
            <span className="text-[11px]">
              יש תשלום שממתין לאישור ({shekels(pay.pending.amountAgorot)}) — אם כבר שילמתם, אין צורך לשלם שוב.
            </span>
          )}
        </div>
      )}

      {note && <div className="text-[12px] mt-2 pr-6">{note}</div>}
    </div>
  );
}
